import _ from 'lodash';
import { AVAILABLE_ORDER_TYPE } from 'v2/common/utils/types.type';
import {
  PARTNER_NAME,
  QuantConfigOrderTypes,
  UiContent,
  RenameProductType,
} from 'v3/common/utils/types.type';
import { ACTIVE_BROKERS_DOMAINS } from 'v3/pages/LandingPage/Default/BannerSection/types.type';

type ApplicationDomainUtilityTypes = Record<
  string,
  {
    label: string;
    value: keyof RenameProductType;
  }
>;

export const LIMIT_ORDER_ONLY_ALLOWED_PARTNER_NAMES = [PARTNER_NAME.ALGOVERSE, PARTNER_NAME.QUBIT];

const ApplicationDomainUtility = (() => {
  const { applicationDomain } = window;
  const config = applicationDomain?.config ?? {};
  const partnerName = _.toLower(config.partner_profile?.name);
  const isAlgoverse = partnerName === PARTNER_NAME.ALGOVERSE;
  const isB2b = !!applicationDomain?.id;
  const isPaperOrderTypeAllowed = isB2b && _.get(window, 'isPrivilegedUser', false);
  const baseOrderTypes = config.ui_content?.allowed_order_type ?? [];
  const isCrypto = _.get(window, 'SA_LOCALE.isCryptoVersion', false);
  const locale = _.get(window, 'SA_LOCALE.locale', 'in');
  const productName = _.get(config, 'ui_content.logoText', 'QuantMan');
  const isIndiaVersion = locale === 'in';
  const isAdmin = _.get(window, 'isAdmin', false);

  return {
    config,
    getUiConfig: <K extends keyof UiContent>(name: K) =>
      applicationDomain?.config?.ui_content?.[name] ?? null,
    isB2b: !!applicationDomain?.id,
    partnerName,
    isStockWiz: partnerName === PARTNER_NAME.STOCKWIZ,
    isAlgoverse,
    isCrypto,
    locale,
    productName,
    isIndiaVersion,
    isQubit: partnerName === PARTNER_NAME.QUBIT,
    shouldShowBacktestInStrategy: config.ui_content?.should_show_backtest_in_strategy || false,
    supportedBrokers: config.brokers || [],
    isUserSignedIn: _.get(window, 'isUserSignedIn', false),
    isAdmin,
    currentUserBroker: _.get(window, 'currentUserBroker', ''),
    eligibleOrderType: () => {
      if (isCrypto) {
        return isPaperOrderTypeAllowed ? ['paper', ...baseOrderTypes] : baseOrderTypes;
      } else {
        if (isAdmin) return [AVAILABLE_ORDER_TYPE.AUTOMATIC, AVAILABLE_ORDER_TYPE.PAPER];
        return config.ui_content?.allowed_order_type ?? [];
      }
    },

    renameProductType: (defaultProductTypes: ApplicationDomainUtilityTypes) => {
      const renameConfig = config.ui_content?.rename_menu?.product_type;
      const renameKeys = _.map(renameConfig, (_, value) => value);
      return _.map(defaultProductTypes, (item) => {
        if (_.includes(renameKeys, item.value)) {
          if (renameConfig) item.label = renameConfig[item.value];
        }
        return item;
      });
    },

    filterQuantConfigOrderTypes: (defaultOrderTypes: QuantConfigOrderTypes) => {
      const allowedTypes =
        config.ui_content?.allowed_quant_config_ordertypes ?? _.map(defaultOrderTypes, 'value');
      return _.filter(defaultOrderTypes, (type) => _.includes(allowedTypes, type.value));
    },

    isLimitOrderOnlyAllowedPartner: LIMIT_ORDER_ONLY_ALLOWED_PARTNER_NAMES.includes(
      partnerName as PARTNER_NAME
    ),

    shouldShowBrokerage: function (): boolean {
      return (
        this.isUserSignedIn &&
        (this.currentUserBroker === ACTIVE_BROKERS_DOMAINS.FYERS || this.currentUserBroker === 'broker_stub')
      );
    },
  };
})();

export default ApplicationDomainUtility;
